import { AnyAction } from 'redux';
import { EffectsCommandMap, Subscription, SubscriptionAPI } from 'dva';
import { DvaReducer } from './shared';
import dayjs from 'dayjs';
import { ConnectState } from '@/models/connect';
import { FUtil, FServiceAPI } from '@freelog/tools-lib';
import { history } from '@@/core/history';
import fMessage from '@/components/fMessage';
import { PolicyFullInfo_Type } from '@/type/contractTypes';
import { IResourceCreateVersionDraftType } from '@/type/resourceTypes';
import { getAttrsInfoByKeys } from '@/utils/service';
import { isRssRelatedResource } from '@/utils/rss';

export interface ResourceVersionEditorPageModelState {
  pageState: 'loading' | 'loaded';
  resourceID: string;
  version: string;
  versions: string[];
  /** RSS 导入单品：停用草稿并锁定编辑 */
  isRssRelated: boolean;

  resourceInfo: {
    resourceID: string;
    resourceName: string;
    resourceType: string[];
  } | null;
  resourceTypeConfig: {
    isSupportDownload: boolean;
    autoGenerateCover: boolean;
  };
  draft: null | IResourceCreateVersionDraftType;
  resourceVersionInfo: {
    version: string;
    sha1: string;
    createData: string;
    mime: string;
  } | null;
  resourceVersionFileInfo: {
    sha1: string;
    mime: string;
    fileName: string;
  } | null;

  descriptionFullScreen: boolean;
  description: string;

  graphShow: boolean;

  // rawProperties: {
  //   key: string;
  //   name: string;
  //   value: string;
  //   description: string;
  // }[];
  // additionalProperties: {
  //   key: string;
  //   name: string;
  //   value: string;
  //   description: string;
  // }[];
  // propertyKeysSort: string[];

  systemProperties: {
    key: string;
    name: string;
    value: string;
    description: string;
    type: 'raw' | 'additional';
    valueConfig?: {
      text?: {
        nullable: boolean;
        minLength: number;
        maxLength: number;
      };
      textArea?: {
        nullable: boolean;
        minLength: number;
        maxLength: number;
      };
      integer?: {
        nullable: boolean;
        min: number;
        max: number;
      };
      decimal?: {
        nullable: boolean;
        minDecimal: number;
        maxDecimal: number;
        precision: number;
      };
      date?: {
        nullable: boolean;
        startDate: string;
        limitDate: string;
      };
      dataTime?: {
        nullable: boolean;
        startDateTime: string;
        limitDateTime: string;
      };
      configText?: {
        nullable: boolean;
        defaultValue: string;
        minLength: number;
        maxLength: number;
      };
      configEnum?: {
        nullable: boolean;
        defaultValue: string;
        options: string[];
      };
    };
  }[];
  customProperties: {
    key: string;
    name: string;
    value: string;
    description: string;
  }[];

  customConfigurations: {
    key: string;
    name: string;
    description: string;
    type: 'input' | 'select';
    input: string;
    select: string[];
  }[];

  directDependencies: {
    id: string;
    name: string;
    type: 'resource' | 'object';
    versionRange?: string;
  }[];
  baseUpcastResources: {
    resourceID: string;
    resourceName: string;
  }[];
  reload: number;
}

export interface ChangeAction extends AnyAction {
  type: 'change' | 'resourceVersionEditorPage/change';
  payload: Partial<ResourceVersionEditorPageModelState>;
}

export interface OnMount_Page_Action extends AnyAction {
  type: 'resourceVersionEditorPage/onMount_Page';
  payload: {
    resourceID: string;
    version: string;
  };
}

export interface OnUnmount_Page_Action extends AnyAction {
  type: 'resourceVersionEditorPage/onUnmount_Page';
}

export interface FetchDataSourceAction extends AnyAction {
  type: 'resourceVersionEditorPage/fetchDataSource' | 'fetchDataSource';
}

export interface OnChange_Version_Action extends AnyAction {
  type: 'resourceVersionEditorPage/onChange_Version';
  payload: {
    version: string;
  };
}

export interface UpdateDataSourceAction extends AnyAction {
  type: 'resourceVersionEditorPage/updateDataSource';
  payload: Partial<Parameters<typeof FServiceAPI.Resource.updateResourceVersionInfo>[0]>;
}

export interface SyncAllPropertiesAction extends AnyAction {
  type: 'syncAllProperties' | 'resourceVersionEditorPage/syncAllProperties';
  payload?: {
    successMessage?: string;
  };
}

export interface ResourceVersionEditorModelType {
  namespace: 'resourceVersionEditorPage';
  state: ResourceVersionEditorPageModelState;
  effects: {
    onMount_Page: (action: OnMount_Page_Action, effects: EffectsCommandMap) => void;
    onUnmount_Page: (action: OnUnmount_Page_Action, effects: EffectsCommandMap) => void;
    fetchDataSource: (action: FetchDataSourceAction, effects: EffectsCommandMap) => void;
    onChange_Version: (action: OnChange_Version_Action, effects: EffectsCommandMap) => void;
    updateDataSource: (action: UpdateDataSourceAction, effects: EffectsCommandMap) => void;
    syncAllProperties: (action: SyncAllPropertiesAction, effects: EffectsCommandMap) => void;
  };
  reducers: {
    // changeDataSource: DvaReducer<ResourceVersionEditorPageModelState, ChangeDataSourceAction>;
    change: DvaReducer<ResourceVersionEditorPageModelState, ChangeAction>;
  };
  subscriptions: { setup: Subscription };
}

const initStates: ResourceVersionEditorPageModelState = {
  pageState: 'loading',
  resourceID: '',
  version: '',
  versions: [],
  isRssRelated: false,
  resourceInfo: null,
  resourceTypeConfig: {
    isSupportDownload: false,
    autoGenerateCover: false,
  },
  draft: null,
  resourceVersionInfo: null,
  resourceVersionFileInfo: null,

  descriptionFullScreen: false,
  description: '',

  graphShow: true,

  systemProperties: [],
  customProperties: [],
  customConfigurations: [],

  directDependencies: [],
  baseUpcastResources: [],
  reload: 0,
};

const Model: ResourceVersionEditorModelType = {
  namespace: 'resourceVersionEditorPage',

  state: initStates,

  effects: {
    *onMount_Page({ payload }: OnMount_Page_Action, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resourceID: payload.resourceID,
          version: payload.version,
        },
      });
      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
      });
    },
    *onUnmount_Page({}: OnUnmount_Page_Action, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: initStates,
      });
    },
    *fetchDataSource({}: FetchDataSourceAction, { call, put, select }: EffectsCommandMap) {
      const { resourceVersionEditorPage }: ConnectState = yield select(
        ({ resourceVersionEditorPage }: ConnectState) => ({
          resourceVersionEditorPage,
        }),
      );

      const params1: Parameters<typeof FServiceAPI.Resource.info>[0] = {
        resourceIdOrName: resourceVersionEditorPage.resourceID,
        isLoadPolicyInfo: 1,
        isTranslate: 1,
      };

      const {
        data: data_resourceInfo,
      }: {
        data: {
          userId: number;
          status: number;
          resourceId: string;
          resourceName: string;
          resourceVersions: {
            version: string;
          }[];
          latestVersion: string;
          coverImages: string[];
          resourceType: string[];
          resourceTypeCode: string;
          policies: PolicyFullInfo_Type[];
          baseUpcastResources: {
            resourceId: string;
            resourceName: string;
          }[];
          feedUrl?: string;
          rssGuid?: string;
          rssPubDate?: string;
        };
      } = yield call(FServiceAPI.Resource.info, params1);

      const isRssRelated = isRssRelatedResource(data_resourceInfo);

      let resourceSelectedVersion: string = resourceVersionEditorPage.version;
      if (data_resourceInfo.latestVersion === '') {
        resourceSelectedVersion = '';
      } else {
        if (resourceSelectedVersion === '') {
          resourceSelectedVersion = data_resourceInfo.latestVersion;
        }
      }

      // RSS 相关资源停用草稿读写
      let data_draft: null | { draftData: IResourceCreateVersionDraftType } = null;
      if (!isRssRelated) {
        const params3: Parameters<typeof FServiceAPI.Resource.lookDraft>[0] = {
          resourceId: resourceVersionEditorPage.resourceID,
        };
        const draftResult: { data: null | { draftData: IResourceCreateVersionDraftType } } =
          yield call(FServiceAPI.Resource.lookDraft, params3);
        data_draft = draftResult.data;
      }

      const params4: Parameters<typeof FServiceAPI.Resource.getResourceTypeInfoByCode>[0] = {
        code: data_resourceInfo.resourceTypeCode,
      };
      const {
        data: data_ResourceTypeInfo,
      }: {
        ret: number;
        errCode: number;
        msg: string;
        data: {
          resourceConfig: {
            fileCommitMode: number[];
            fileMaxSize: number;
            fileMaxSizeUnit: 1 | 2;
            supportDownload: 1 | 2;
            supportEdit: 1 | 2;
            supportOptionalConfig: 1 | 2;
            autoGenerateCover: 1 | 2;
          };
          attrs: {
            key: string;
            name: string;
          }[];
        };
      } = yield call(FServiceAPI.Resource.getResourceTypeInfoByCode, params4);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          version: resourceSelectedVersion,
          isRssRelated,
          draft: data_draft ? data_draft.draftData : null,
          resourceTypeConfig: {
            isSupportDownload: data_ResourceTypeInfo.resourceConfig.supportDownload === 2,
            autoGenerateCover: data_ResourceTypeInfo.resourceConfig.autoGenerateCover === 2,
          },
        },
      });
      if (resourceSelectedVersion === '') {
        yield put<ChangeAction>({
          type: 'change',
          payload: {
            pageState: 'loaded',
            // version: resourceSelectedVersion,
          },
        });
        return;
      }

      const params: Parameters<typeof FServiceAPI.Resource.resourceVersionInfo1>[0] = {
        resourceId: resourceVersionEditorPage.resourceID,
        version: resourceSelectedVersion,
      };
      const {
        data: data_versionInfo,
      }: {
        data: {
          resourceId: string;
          resourceName: string;
          resourceType: string[];
          resourceTypeCode: string;
          version: string;
          fileSha1: string;
          filename: string;
          customPropertyDescriptors: {
            key: string;
            name: string;
            defaultValue: string;
            type: 'editableText' | 'readonlyText' | 'radio' | 'checkbox' | 'select';
            candidateItems: string[];
            remark: string;
          }[];
          createDate: string;
          description: string;
          systemPropertyDescriptors: {
            defaultValue: number | string;
            key: string;
            name: string;
            remark: string;
            valueDisplay: string;
            valueUnit: string;
            insertMode: 1 | 2;
          }[];
          dependencies: {
            resourceId: string;
            resourceName: string;
            versionRange: string;
          }[];
          versionProperty: {
            mime: string;
          };
        };
      } = yield call(FServiceAPI.Resource.resourceVersionInfo1, params);
      if (!data_versionInfo) {
        history.replace(FUtil.LinkTo.exception403({}));
        return;
      }

      const base = data_versionInfo.customPropertyDescriptors.filter(
        (i) => i.type === 'readonlyText',
      );
      const opt = data_versionInfo.customPropertyDescriptors.filter(
        (i) => i.type === 'editableText' || i.type === 'select',
      );

      const params2: Parameters<typeof getAttrsInfoByKeys>[0] = {
        keys: data_versionInfo.systemPropertyDescriptors
          .filter((b) => b.insertMode === 2)
          .map((b) => b.key),
      };
      const attrsInfo: Awaited<ReturnType<typeof getAttrsInfoByKeys>> = yield call(
        getAttrsInfoByKeys,
        params2,
      );

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          // pageState: 'loaded',
          versions: data_resourceInfo.resourceVersions
            .map((v) => {
              return v.version;
            })
            .reverse(),
          resourceInfo: {
            resourceID: data_versionInfo.resourceId,
            resourceName: data_versionInfo.resourceName,
            resourceType: data_versionInfo.resourceType,
          },

          resourceVersionInfo: {
            version: data_versionInfo.version,
            sha1: data_versionInfo.fileSha1,
            createData: dayjs(data_versionInfo.createDate).format(FUtil.Predefined.momentDateFormat),
            // createData: '2020-12-12',
            mime: data_versionInfo.versionProperty.mime,
          },
          resourceVersionFileInfo: {
            sha1: data_versionInfo.fileSha1,
            mime: data_versionInfo.versionProperty.mime,
            fileName: data_versionInfo.filename,
          },
          description: data_versionInfo.description,
          systemProperties: data_versionInfo.systemPropertyDescriptors.map((spd) => {
            return {
              key: spd.key,
              name: spd.name,
              value: spd.valueDisplay,
              description: spd.remark,
              type: spd.insertMode === 1 ? 'raw' : 'additional',
              valueConfig: attrsInfo.find((i) => {
                return i.key === spd.key;
              })?.valueConfig || {
                text: {
                  nullable: true,
                  minLength: 0,
                  maxLength: 100,
                },
              },
            };
          }),
          customProperties: base.map((b) => {
            return {
              key: b.key,
              name: b.name,
              value: b.defaultValue,
              description: b.remark,
            };
          }),
          customConfigurations: opt.map((i) => ({
            key: i.key,
            name: i.name,
            description: i.remark,
            type: i.type === 'editableText' ? 'input' : 'select',
            input: i.defaultValue,
            select: i.candidateItems,
          })),

          directDependencies: data_versionInfo.dependencies.map((r) => {
            return {
              id: r.resourceId,
              name: r.resourceName,
              type: 'resource',
              versionRange: r.versionRange,
            };
          }),
          baseUpcastResources: data_resourceInfo.baseUpcastResources.map((b) => {
            return {
              resourceID: b.resourceId,
              resourceName: b.resourceName,
            };
          }),
          reload: 0,
        },
      });

      yield call(FUtil.Tool.promiseSleep, 1000);

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          pageState: 'loaded',
          reload: 1,
        },
      });
    },
    *onChange_Version({ payload }: OnChange_Version_Action, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          version: payload.version,
        },
      });
      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
      });
    },
    *updateDataSource(action: UpdateDataSourceAction, { call, put, select }: EffectsCommandMap) {
      const baseInfo: { version: string; resourceId: string } = yield select(
        ({ resourceVersionEditorPage }: ConnectState) => ({
          version: resourceVersionEditorPage.version,
          resourceId: resourceVersionEditorPage.resourceID,
        }),
      );
      const params: Parameters<typeof FServiceAPI.Resource.updateResourceVersionInfo>[0] = {
        ...baseInfo,
        ...action.payload,
        inputAttrs: action.payload.inputAttrs || [],
      };
      yield call(FServiceAPI.Resource.updateResourceVersionInfo, params);
      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
        payload: baseInfo,
      });
    },
    *syncAllProperties({ payload }: SyncAllPropertiesAction, { select, call }: EffectsCommandMap) {
      const { resourceVersionEditorPage }: ConnectState = yield select(
        ({ resourceVersionEditorPage }: ConnectState) => ({
          resourceVersionEditorPage,
        }),
      );

      const customPropertyDescriptors: Parameters<
        typeof FServiceAPI.Resource.updateResourceVersionInfo
      >[0]['customPropertyDescriptors'] = [
        ...resourceVersionEditorPage.customProperties.map((bp) => {
          return {
            key: bp.key,
            name: bp.name,
            defaultValue: bp.value,
            type: 'readonlyText' as 'readonlyText',
            remark: bp.description,
          };
        }),
        ...resourceVersionEditorPage.customConfigurations.map((pp) => {
          const isInput: boolean = pp.type === 'input';
          const options: string[] = pp.select;
          return {
            type: isInput ? 'editableText' : ('select' as 'editableText' | 'select'),
            key: pp.key,
            name: pp.name,
            remark: pp.description,
            defaultValue: isInput ? pp.input : options[0],
            candidateItems: isInput ? undefined : options,
          };
        }),
      ];

      const params: Parameters<typeof FServiceAPI.Resource.updateResourceVersionInfo>[0] = {
        version: resourceVersionEditorPage.version,
        resourceId: resourceVersionEditorPage.resourceID,
        customPropertyDescriptors: customPropertyDescriptors,
        inputAttrs: resourceVersionEditorPage.systemProperties
          .filter((sp) => sp.type === 'additional')
          .map((ap) => {
            return {
              key: ap.key,
              value: ap.value,
            };
          }),
      };
      const { ret, errCode, data, msg } = yield call(
        FServiceAPI.Resource.updateResourceVersionInfo,
        params,
      );
      if (ret !== 0 || errCode !== 0) {
        return fMessage(msg, 'error');
      }
      if (payload?.successMessage) {
        fMessage(payload?.successMessage, 'success');
      }
    },
  },

  reducers: {
    // changeDataSource(state: ResourceVersionEditorPageModelState, action: ChangeDataSourceAction): ResourceVersionEditorPageModelState {
    //   return {...state, ...action.payload};
    // },
    change(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },

  subscriptions: {
    setup({ dispatch, history }: SubscriptionAPI) {
      // history.listen((listener) => {
      //   const regexp = pathToRegexp('/resource/:id/$version/:$version');
      //   const result = regexp.exec(listener.pathname);
      //   if (result) {
      //     if (result[2] === 'creator') {
      //       return;
      //     }
      //     dispatch<FetchDataSourceAction>({
      //       type: 'fetchDataSource',
      //       payload: {
      //         resourceId: result[1],
      //         $version: result[2],
      //       }
      //     });
      //   }
      // });
    },
  },
};

export default Model;
